import { useAuth } from '../contexts/AuthContext';
import { useRBAC } from './useRBAC';
import { useModuleAvailability } from './useModuleAvailability';
import { assignedUserIdOf } from '../utils/riskWorkflow';

// Who may do what to a risk — the additive rule the server enforces (organization, deal or business
// unit role, or being the assignee), mirrored for the UI. Shared by every page that works a risk, so
// the Risks page and GRC never show a different set of actions on the same risk.
export const useRiskWorkflow = () => {
    const { user } = useAuth();
    const { canManageOrganizationRisks, canManageDeal, canManageBusinessUnit, canUpdateRiskViaAssignment } = useRBAC();
    const canManageOrgRisks = canManageOrganizationRisks();
    // Jira / ServiceNow need internet access: their buttons are hidden where ticketing is unavailable.
    const ticketingAvailable = useModuleAvailability().isModuleAvailable('ticketing');

    // !! coercion: dealId/businessUnitId can be 0/null for org-level rows, and `0 && x` returns the
    // number 0 — which React renders as a literal "0" in `{cond && <X/>}`.
    const canManageRisk = (risk) => !!(canManageOrgRisks
        || (risk.businessUnitId && canManageBusinessUnit(risk.businessUnitId))
        || (risk.dealId && canManageDeal(risk.dealId)));
    const canUpdateRisk = (risk) => !!(canManageRisk(risk)
        || canUpdateRiskViaAssignment(assignedUserIdOf(risk)));
    const canApproveRisk = (risk) =>
        (risk.primaryApprovalStatus === 1 && risk.primaryApproverUserId === user?.id) ||
        (risk.secondaryApprovalStatus === 1 && risk.secondaryApproverUserId === user?.id);

    return { canManageOrgRisks, canManageRisk, canUpdateRisk, canApproveRisk, ticketingAvailable };
};
